'use client';
// HowItWorksModal Component - Multi-step tutorial modal for non-authenticated users
import { useState } from 'react';
import Stepper from 'react-stepper-horizontal';
import { useRouter } from 'next/navigation';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';

import { useBodyScrollLock, useT } from '@/hooks';
import { openModal, useAppDispatch } from '@/store';

import { HOW_IT_WORKS_STEPS, ROUTES } from '@/constants';

interface HowItWorksModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface StepContentProps {
  activeStep: number;
  primaryButtonStyles: Record<string, unknown>;
}

interface BackButtonProps {
  activeStep: number;
  onBack: () => void;
}

interface NextButtonProps {
  activeStep: number;
  onNext: () => void;
  onGetStarted: () => void;
  primaryButtonStyles: Record<string, unknown>;
}

const StepContent = ({ activeStep, primaryButtonStyles }: StepContentProps) => {
  const { t } = useT();
  switch (activeStep) {
    case 0:
      return (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ mb: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
          >
            {t('howItWorks.step1Title')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '0.95rem' }, lineHeight: 1.5 }}
          >
            {t('howItWorks.step1Description')}
          </Typography>
        </Box>
      );

    case 1:
      return (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          {/* Betting Cards Visual */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              mb: 3,
              position: 'relative',
              minHeight: 180,
              alignItems: 'flex-start',
            }}
          >
            {/* Buy Yes Card */}
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                p: { xs: 1.5, sm: 2 },
                boxShadow: 2,
                border: '1px solid',
                borderColor: 'divider',
                width: { xs: 130, sm: 160 },
                position: 'relative',
                zIndex: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    bgcolor: 'action.hover',
                    width: 28,
                    height: 28,
                    color: 'text.secondary',
                  }}
                >
                  -
                </IconButton>
                <Typography variant="h5" fontWeight={700}>
                  $100
                </Typography>
                <IconButton
                  size="small"
                  sx={{
                    bgcolor: 'action.hover',
                    width: 28,
                    height: 28,
                    color: 'text.secondary',
                  }}
                >
                  +
                </IconButton>
              </Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('howItWorks.toWin')}
              </Typography>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                color="success.main"
                sx={{ mb: 1.5 }}
              >
                $400
              </Typography>
              <Button fullWidth size="small" sx={{ ...primaryButtonStyles, py: 1 }}>
                {t('howItWorks.buyYes')}
              </Button>
            </Box>

            {/* Buy No Card (behind) */}
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                p: { xs: 1.5, sm: 2 },
                boxShadow: 1,
                border: '1px solid',
                borderColor: 'divider',
                width: { xs: 130, sm: 160 },
                position: 'relative',
                mt: 1.5,
                ml: -2,
                zIndex: 1,
                opacity: 0.9,
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}
              >
                <IconButton
                  size="small"
                  sx={{
                    bgcolor: 'action.hover',
                    width: 28,
                    height: 28,
                    color: 'text.secondary',
                  }}
                >
                  +
                </IconButton>
              </Box>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                $100
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('howItWorks.win')}
              </Typography>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                color="text.primary"
                sx={{ mb: 1.5 }}
              >
                $133
              </Typography>
              <Button fullWidth size="small" sx={{ ...primaryButtonStyles, py: 1 }}>
                {t('howItWorks.buyNo')}
              </Button>
            </Box>
          </Box>

          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ mb: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
          >
            {t('howItWorks.step2Title')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '0.95rem' }, lineHeight: 1.5 }}
          >
            {t('howItWorks.step2Description')}
          </Typography>
        </Box>
      );

    case 2:
      return (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          {/* Cash Out Section */}
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              p: 2,
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 0,
                right: 0,
                height: 16,
                background: `repeating-linear-gradient(
                  45deg,
                  transparent,
                  transparent 8px,
                  ${(theme) => theme.palette.divider} 8px,
                  ${(theme) => theme.palette.divider} 16px
                )`,
                clipPath: 'polygon(0 0, 100% 0, 95% 100%, 5% 100%)',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1.5,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                Will there be a US recession this year?
              </Typography>
              <TrendingUpIcon sx={{ color: 'success.main', fontSize: 20 }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('howItWorks.odds')}
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    px: 1.5,
                  }}
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('howItWorks.bet')}
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    px: 1.5,
                  }}
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('howItWorks.toWin')}
                </Typography>
                <Typography variant="h6" fontWeight={700} color="success.main">
                  $250
                </Typography>
              </Box>
            </Box>
            <Button fullWidth size="small" sx={{ ...primaryButtonStyles, py: 1 }}>
              {t('howItWorks.cashOut')}
            </Button>
          </Box>

          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ mb: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
          >
            {t('howItWorks.step3Title')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '0.95rem' }, lineHeight: 1.5 }}
          >
            {t('howItWorks.step3Description')}
          </Typography>
        </Box>
      );

    default:
      return null;
  }
};

const NextButton = ({ activeStep, onNext, onGetStarted, primaryButtonStyles }: NextButtonProps) => {
  const { t } = useT();
  if (activeStep < HOW_IT_WORKS_STEPS.length - 1) {
    return (
      <Button
        onClick={onNext}
        sx={{
          ...primaryButtonStyles,
          px: 4,
          borderRadius: 2,
        }}
      >
        {t('howItWorks.next')}
      </Button>
    );
  }
  return (
    <Button
      onClick={onGetStarted}
      sx={{
        ...primaryButtonStyles,
        px: 4,
        borderRadius: 2,
      }}
    >
      {t('howItWorks.getStarted')}
    </Button>
  );
};

// No longer using StepperSteps component

export const HowItWorksModal = ({ isOpen, onClose }: HowItWorksModalProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const router = useRouter();
  const theme = useTheme();
  const dispatch = useAppDispatch();

  useBodyScrollLock(isOpen);

  const handleNext = () => {
    if (activeStep < HOW_IT_WORKS_STEPS.length - 1) {
      setActiveStep((prev) => prev + 1);
    } else {
      onClose();
      router.push(ROUTES.HOME);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    onClose();
  };

  const handleGetStarted = () => {
    onClose();
    dispatch(openModal('signIn'));
  };

  const primaryButtonStyles = {
    background: theme.palette.primary.main,
    bgcolor: theme.palette.primary.main,
    color: theme.palette.common.white,
    textTransform: 'none' as const,
    fontWeight: 600,
    py: 1.5,
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
    '&:hover': {
      background: theme.palette.primary.dark,
      bgcolor: theme.palette.primary.dark,
      boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.5)}`,
    },
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          bgcolor: 'background.paper',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: alpha(theme.palette.common.black, 0.7),
            backdropFilter: 'blur(4px)',
          },
        },
      }}
    >
      {/* Fixed Header with Stepper */}
      <Box
        sx={{
          py: 3,
          px: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'relative',
          zIndex: 11,
        }}
      >
        <Box sx={{ width: '100%', mb: -1 }}>
          <Stepper
            steps={HOW_IT_WORKS_STEPS.map((step) => ({ title: step.title }))}
            activeStep={activeStep}
            activeColor={theme.palette.primary.main}
            completeColor={theme.palette.primary.main}
            defaultColor={theme.palette.action.disabledBackground}
            activeTitleColor={theme.palette.text.primary}
            completeTitleColor={theme.palette.text.primary}
            defaultTitleColor={theme.palette.text.secondary}
            circleFontSize={14}
            size={32}
            circleTop={0}
            titleFontSize={0} // Hide titles for slim look
          />
        </Box>

        <IconButton
          onClick={handleClose}
          aria-label="Close"
          sx={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'text.secondary',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, position: 'relative', zIndex: 1, overflow: 'auto' }}>
        {/* Step Content */}
        <Box sx={{ px: 4, pt: 2, pb: 2 }}>
          <StepContent activeStep={activeStep} primaryButtonStyles={primaryButtonStyles} />

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'end', mt: 1.5 }}>
            <NextButton
              activeStep={activeStep}
              onNext={handleNext}
              onGetStarted={handleGetStarted}
              primaryButtonStyles={primaryButtonStyles}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default HowItWorksModal;
